<template>
    <div class="modal fade fixed-end" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-vertical dw-md-8 dw-lg-6 dw-xl-5" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="drawer-title">
                        {{ editing ? 'Edit' : 'Add' }} Activity Log Item
                    </h2>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <div class="modal-body">
                    <div class="mb-3">
                        <label class="control-label">Type</label>
                        <multiselect
                            v-model="stateType"
                            :options="types"
                            :showLabels="false"
                            placeholder=""
                            label="text"
                            track-by="id"
                            name="type"
                            v-validate="'required'">
                        </multiselect>

                        <help-block field="type"/>
                    </div>

                    <div class="mb-3">
                        <label class="control-label">{{ titleLabelTitle }}</label>
                        <input
                            name="title"
                            type="text"
                            class="form-control"
                            v-model="activity.title"
                            v-validate="'required'" />

                        <help-block field="title"/>
                    </div>

                    <div class="mb-3" v-if="shouldShowStatus">
                        <label class="control-label">Status</label>
                        <multiselect
                            v-model="stateStatus"
                            :options="statuses"
                            :showLabels="false"
                            placeholder=""
                            label="text"
                            track-by="id"
                            name="status"
                            v-validate="'required'">
                        </multiselect>

                        <help-block field="status"/>
                    </div>

                    <div class="row">
                        <div class="col-md-6 mb-3" v-if="shouldShowDueDate">
                            <label class="control-label">{{ dueDateLabelTitle }}</label>
                            <datepicker
                                name="due_date"
                                v-model="activity.due_date"
                                input-class="form-control"
                                data-vv-as="due date"
                                :clear-button="true"
                                v-validate="'required'"
                            />
                        </div>

                        <div class="col-md-6 mb-3" v-if="shouldShowAchievedDate">
                            <label class="control-label">{{ achievedDateLabelTitle }}</label>
                            <datepicker
                                name="achieved"
                                v-model="activity.achieved"
                                input-class="form-control"
                                data-vv-as="achieved date"
                                :clear-button="true"
                            />
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="control-label">Content</label>
                        <textarea
                            name="content"
                            type="text"
                            class="form-control"
                            rows="6"
                            v-model="activity.content" />

                        <help-block field="content"/>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-light" @click="handleCancel">Cancel</button>
                        <c-button @click="handleCreateLogItem" class="btn btn-rounded btn-primary">Save</c-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { LaddaButton } from 'components/form/input'
import Multiselect from 'vue-multiselect'
import Datepicker from 'vuejs-datepicker'
import { notify } from 'utils'
import { activityLog } from 'mixins'
import { cloneDeep, filter, first } from 'lodash'
import { CModal } from '@chameleon/chameleon-vue'

export default {
    extends: CModal,

    $_veeValidate: {
        validator: 'new' // give me my own validator scope.
    },

    mixins: [activityLog],

    props: ['editActivity'],

    components: {
        LaddaButton,
        Multiselect,
        Datepicker
    },

    data () {
        return {
            activity: {},
            types: [
                { 'id': 'generic', 'text': 'Generic Activity' },
                { 'id': 'milestone', 'text': 'Milestone Activity' },
                { 'id': 'meeting', 'text': 'Meeting Activity' },
                { 'id': 'mail', 'text': 'Mail Activity' }
            ],
            statuses: [
                { 'id': 'na', 'text': 'Not Applicable' },
                { 'id': 'pending', 'text': 'Pending' },
                { 'id': 'rescheduled', 'text': 'Re-scheduled' },
                { 'id': 'delayed', 'text': 'Delayed' },
                { 'id': 'completed', 'text': 'Completed' }
            ]
        }
    },

    watch: {
        editActivity(value) {
            this.activity = cloneDeep(value)
        }
    },

    mounted() {
        this.activity = cloneDeep(this.editActivity)

        this.addEventListener('open', () => {
            this.activity = cloneDeep(this.editActivity)
        })
    },

    computed: {
        ...mapState('yodizproject', {
            project: 'single'
        }),

        editing: {
            get() {

                return this.activity.id ? true : false
            }
        },

        stateType: {
            get() {
                return this.typeForId(this.activity.type)
            },

            set(value) {
                if (value !== null) {

                    this.activity.type = value.id
                } else {
                    this.activity.type = null
                }
            }
        },

        stateStatus: {
            get() {
                return this.statusForId(this.activity.status)
            },

            set(value) {
                if (value !== null) {
                    this.activity.status = value.id
                } else {
                    this.activity.status = null
                }
            }
        }
    },

    methods: {
        ...mapActions('projects/activityLog', ['createActivityLogItem', 'updateActivityLogItem']),

        handleCancel () {
            this.close()
        },

        statusForId(id) {
            var found = filter(this.statuses, (item) => { return item.id == id })
            return found.length > 0 ? first(found) : null
        },

        typeForId(id) {
            var found = filter(this.types, (item) => { return item.id == id })
            return found.length > 0 ? first(found) : null
        },

        async handleCreateLogItem() {
            if (!await this._validate()) {
                notify.error('Form validation failed, please check your inputs and try again.')
                return Promise.reject()
            }

            this.activity.due_date = this.activity.due_date ? moment(this.activity.due_date).format('YYYY-MM-DD') : null
            this.activity.achieved = this.activity.achieved ? moment(this.activity.achieved).format('YYYY-MM-DD') : null

            if (this.activity.type === "milestone") {
                this.activity.status = "na"
            }

            let payload = {
                projectId: this.project.id,
                payload: this.activity
            }

            if (!this.editing) {

                return this.createActivityLogItem(payload).then((response) => {
                    //close side drawer
                    notify.success('New activity log successfully created.')
                    this.close()

                }).catch((error) => {
                    if (error.response.status === 422) {
                        notify.error('Form validation failed, please check your inputs and try again.')
                    } else {
                        notify.error('Failed to create activity log item, please try again.')
                    }
                })
            } else {
                payload.id = this.activity.id

                return this.updateActivityLogItem(payload).then((response) => {
                     //close side drawer
                    notify.success('New activity log successfully created.')
                    this.close()

                }).catch((error) => {
                    if (error.response.status === 422) {
                        notify.error('Form validation failed, please check your inputs and try again.')
                    } else {
                        notify.error('Failed to create activity log item, please try again.')
                    }
                })
            }
        }
    }
}
</script>
